import { Back, Send } from "iconsax-react";
import React, { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import instance, { url } from "../../../axios/instance";
import { ulid } from "ulid";
import { getToken } from "../../../utils/token";
import { useRevalidator } from "react-router-dom";
import { MessageInput } from "./MessageInput";
import { getFileType, getFileTypeIcon } from "./file-utils";

const MessageItem = ({
  order,
  onClose,
  dbMessages,
  doctor,
  isOpen,
  setUnreadMessages,
}) => {
  const facilityId = order.facility_id;
  const pharmacyId = order.pharmacy_id;
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState(dbMessages);
  const [socket, setSocket] = useState(null);
  const messagesEndRef = useRef(null);
  const revalidator = useRevalidator();

  // Function to scroll to the bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Scroll to the bottom on initial load and whenever the messages array changes
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const newSocket = io(url, {
      auth: {
        orderId: order.id,
        pharmacyId,
        facilityId,
      },
    });

    setSocket(newSocket);

    newSocket.on("message", (value) => {
      const message = {
        id: value.id,
        msg: value.msg,
        sender_id: pharmacyId,
        recipient_id: facilityId,
        facilityAdmin: "",
        pharmacyAdmin: value.from,
        isFile: value.isFile,
        file_path: value.filePath,
        file_name: value.fileName,
        file_size: value.fileSize,
        file_type: value.fileType,
        created_at: value.createdAt,
      };
      setMessages((messages) => [...messages, message]);
    });

    scrollToBottom();
    return () => {
      newSocket.close();
      newSocket.off("message");
    };
  }, []);

  useEffect(() => {
    if (socket) {
      if (isOpen) {
        socket.emit("doctor-online-status", {
          senderId: facilityId,
          status: "online",
        });
      } else {
        socket.emit("doctor-online-status", {
          senderId: facilityId,
          status: "offline",
        });
      }
    }
  }, [isOpen, socket]);

  const getMessagePayloadAndEvent = (message) => {
    if (!message) return;

    const id = ulid();
    const createdAt = Date.now();
    const messagePayload = {
      id,
      createdAt,
      facilityId,
      facilityDoctorName: `${doctor.lastname} ${doctor.firstname}`,
      facilityDoctorId: doctor.id,
      msg: message,
      pharmacyId,
    };

    const staticMessagePayload = {
      id,
      sender_id: facilityId,
      recipient_id: pharmacyId,
      pharmacyAdmin: "",
      facilityAdmin: `${doctor.lastname} ${doctor.firstname}`,
      msg: message,
      created_at: createdAt,
    };

    const event = "clinic-message";

    return { messagePayload, staticMessagePayload, event };
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    const { messagePayload, staticMessagePayload, event } =
      getMessagePayloadAndEvent(message);

    socket.emit(event, messagePayload);
    setMessages((messages) => [...messages, staticMessagePayload]);
    setMessage("");
  };

  const handleClose = async () => {
    onClose();
    const token = getToken();
    try {
      await instance.put(
        "/doctor/messages/read",
        { orderId: order.id, senderId: pharmacyId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      revalidator.revalidate();
      setUnreadMessages([]);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div
      className={`fixed bottom-0 right-0 w-[450px] h-[100vh] bg-white border-2 border-primary-light ${
        isOpen ? "translate-y-0" : "translate-y-[150%]"
      } duration-300 ease-in-out`}
    >
      <div className="border-b border-[#eee] py-3 px-5 flex items-center gap-x-2">
        <button onClick={handleClose}>
          <Back size="20" color="black" />
        </button>
        <h2>{order.pharmacy.pharmacy_name} Pharmacy</h2>
      </div>
      <div className="max-h-[85vh] overflow-y-scroll scrollbar-hide px-5 pb-20">
        {messages.length <= 0 ? (
          <div className="mt-3 text-xs opacity-60">
            Start a conversation with this pharmacy
          </div>
        ) : (
          messages.map((message, index) => (
            <div
              className={`py-3 ${
                message.sender_id === facilityId
                  ? "bg-[#f4f4f4] -mx-5 px-5"
                  : ""
              }`}
              key={index}
            >
              <div className="flex justify-between items-center">
                <span className="font-medium text-[#777] text-xs block mb-1 capitalize">
                  {message.sender_id === facilityId
                    ? `You - ${message.facilityAdmin}`
                    : message.pharmacyAdmin}
                </span>
                <span className="font-normal text-[#777] text-xs block mb-1">
                  {new Date(message.created_at).toLocaleString()}
                </span>
              </div>
              {message.isFile ? (
                <div>
                  {message.pending ? (
                    <div className="flex items-center gap-x-2">
                      <span className="">Uploading...</span>
                    </div>
                  ) : (
                    <>
                      {message.file_type?.startsWith("image/") ? (
                        <>
                          <img
                            src={`${url}${message.file_path}`}
                            alt="Preview"
                            className="w-full h-auto rounded-md my-2"
                          />
                          {message.file_size && (
                            <span className="text-xs text-gray-500">
                              {(message.file_size / 1024 / 1024).toFixed(2)} MB
                            </span>
                          )}
                        </>
                      ) : (
                        <div className="flex items-center gap-x-1">
                          <span className="block text-sm font-medium">
                            {getFileType(message.file_type)}
                          </span>
                          {message.file_size && (
                            <span className="text-xs text-gray-500">
                              {(message.file_size / 1024 / 1024).toFixed(2)} MB
                            </span>
                          )}
                        </div>
                      )}

                      {message.file_path && (
                        <a
                          href={`${url}${message.file_path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-x-1 mb-1 hover:underline w-fit"
                        >
                          {message.file_type &&
                            getFileTypeIcon(message.file_type)}
                          <span className="text-gray-500 text-sm">
                            {message.file_name}
                          </span>
                        </a>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <p className="text-sm">{message.msg}</p>
              )}
            </div>
          ))
        )}
      </div>

      <MessageInput
        message={message}
        setMessage={setMessage}
        handleSubmit={handleSubmit}
        getMessagePayloadAndEvent={getMessagePayloadAndEvent}
        setMessages={setMessages}
        socket={socket}
      />
      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessageItem;
